import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChange,
    SimpleChanges,
} from '@angular/core';
import * as L from 'leaflet';
import 'leaflet.pm';
import 'leaflet-control-geocoder';
import 'leaflet.markercluster';
import 'leaflet-routing-machine';
import { DataService } from 'src/app/services/data.service';
import { PopOverTemplatesService } from '../../services/pop-over-templates.service';
import { GeneralService } from 'src/app/services/general.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { LoaderHandlerService, ServicecallService } from 'src/app';
import { ApiConstants } from 'src/app/constants/api-constants';
import { LatLngExpression } from 'leaflet';
import { EntitiesServiceService, EntitySiteService } from '../../services';

declare module 'leaflet' {
    namespace Routing {
        function control(options?: any): any;
    }

    namespace Control {
        function geocoder(options?: any): any;
    }

    interface MarkerClusterGroup extends FeatureGroup {
        addLayer(layer: Layer): this;
        clearLayers(): this;
        removeLayer(layer: Layer): this;
        onAdd(map: Map): this;
        onRemove(map: Map): this;
    }

    function markerClusterGroup(options?: any): MarkerClusterGroup;
}
delete L.Icon.Default.prototype.options.iconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'assets/images/marker-icon-2x.png',
    iconUrl: 'assets/images/marker-icon.png',
    shadowUrl: 'assets/images/marker-shadow.png',
});

@Component({
    selector: 'app-leaflet-map-vot',
    templateUrl: './leaflet-map-vot.component.html',
    styleUrls: ['./leaflet-map-vot.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LeafletMapVotComponent implements OnInit, OnChanges, OnDestroy {
    @Input() showControls: boolean;
    @Input() zoneData: { siteId: string; msg: string; item: any };
    @Input() zoneLength: number;
    @Input() popOver: boolean;
    @Input() pageType: any;
    @Input() mapData: any;
    @Input() zoneType: string = 'add';
    @Input() defaultLocation: any = [8.534796, 76.884992];
    @Input() styleType: string = 'default';
    @Input() hierarchy: boolean = false;
    @Input()
    set showZonesFlag(showZonesFlag: boolean) {
        this._showZones = showZonesFlag;
        this._leafletPloygon();
    }
    @Input() trackerZoneData: any[] = [];
    @Input() isPlaying: boolean;
    @Input() isPaused: boolean;
    @Input() showStartEnd: boolean;
    @Input() showAiAssist: boolean;
    @Input() tripAssistPlots: [number, number][];
    @Input() aggregatePayload: any = {};

    @Output() drawZoneAction = new EventEmitter<any>();
    @Output() removeZoneAction = new EventEmitter<any>();

    private _map: L.Map | any;
    private _markerCluster: any = null;
    private _markerList: any;
    private _vehicleRoute: any;
    private _polygonArray: any[] = [];
    private _circleArray = [];
    private _existingPolygonArray = [];
    private _existingCircleArray = [];
    private _existingZoneData = [];
    private _selectedZoneName: string;
    private _zoneNameToRemove: string;
    private _subscription: Subscription;
    private _circleLayers: Map<string, L.Circle> = new Map();
    private _polygonLayers: Map<string, L.Polygon> = new Map();
    private _showZones: boolean;
    private _trackerzoneNameArray: string[] = [];
    private _tenantConfig: any;
    private traverseMarker: any;
    private isUserInteracting: boolean = false;
    private lastValidAngle: number;
    private _initialBounds?: L.LatLngBounds;

    private _lastMapData: any[] = [];
    private _markersCache: Map<string, L.Marker> = new Map();
    private _isMapInitialized: boolean = false;

    constructor(
        private _dataSer: DataService,
        private _leafletService: PopOverTemplatesService,
        private _generalService: GeneralService,
        private _service: ServicecallService,
        private _loaderHandler: LoaderHandlerService,
        private _cdr: ChangeDetectorRef,
        private _entityService: EntitiesServiceService
    ) {
        this._subscription = this._dataSer?.events?.subscribe((res: any) => {
            if (res?.name === 'ExistingZoneAdded') {
                this._existingZoneData = [];
                res?.args?.[0]?.forEach((zone: any) => {
                    this._existingZoneData?.push(zone);
                    this._existingPolygonArray = [];
                    this._existingCircleArray = [];
                    this._getExistingZoneDetailsGeoTrace(zone);
                });
            } else if (res?.name === 'zoneRemoved') {
                this._zoneNameToRemove = res?.args?.[1];
                this._removeZone();
            }
        });
    }

    ngOnInit() { }

    ngOnChanges(changes: SimpleChanges) {
        this._tenantConfig = this._service.getConfiguration('tenantGenericConfig')
            ? this._service.getConfiguration('tenantGenericConfig')
            : null;
        // ✅ Check if critical properties changed requiring full reinit
        // const needsReinit = this._needsMapReinitialization(changes);

        // if (needsReinit) {
        //     this._performFullReinitialization();
        //     return;
        // }

        // this._performFullReinitialization();

        // ✅ Handle data-only updates efficiently
        if (changes['mapData'] && !changes['mapData'].firstChange) {
            this._handleDataUpdate();
            return;
        }

        // ✅ Handle zone visibility toggle
        if (changes['showZonesFlag'] && !changes['showZonesFlag'].firstChange) {
            this._leafletPloygon();
            this._cdr.markForCheck();
            return;
        }

        // ✅ Handle trip playback updates
        if ((this.isPlaying || this.isPaused) && this._map) {
            this._handleTripPlayback();
            this._cdr.markForCheck();
            return;
        }

        // ✅ First time initialization
        if (!this._isMapInitialized) {
            this._performFullReinitialization();
        }
    }

    ngOnDestroy() {
        if (this._map) this._map?.remove();
        this._subscription?.unsubscribe();
    }

    private _needsMapReinitialization(changes: SimpleChanges): boolean | SimpleChange {
        // Only reinitialize if these critical properties changed
        return (
            changes['pageType'] ||
            changes['styleType'] ||
            changes['defaultLocation'] ||
            changes['zoneType'] ||
            !this._isMapInitialized
        );
    }

    // 6. ADD method for full reinitialization
    private _performFullReinitialization() {

        if (this._map) {
            this._cleanupMap();
        }

        this._markerList = [...this.mapData];

        setTimeout(() => {
            this._isMapInitialized = true;
            this._lastMapData = [...this.mapData];
            this._initialize();
            this._cdr.markForCheck();
        }, 0);
    }

    // 7. ADD method for efficient data updates
    private _handleDataUpdate() {
        this._markerList = [...this.mapData];

        // ✅ Check if data actually changed
        if (!this._hasDataChanged()) {
            return;
        }

        // If map exists, just update markers
        if (this._map) {
            if (!this._markerCluster) {
                this._markerCluster = L.markerClusterGroup({
                    iconCreateFunction: (cluster) => {
                        return this._createClusterIcon(cluster);
                    }
                });
                this._map.addLayer(this._markerCluster);
            }

            this._updateMarkersEfficiently();
            this._lastMapData = [...this.mapData];
        } else {
            // If map doesn't exist, initialize it
            this._performFullReinitialization();
        }

        this._cdr.markForCheck();
    }

    // 8. ADD method to check if data changed
    private _hasDataChanged(): boolean {
        if (this._lastMapData.length !== this.mapData.length) {
            return true;
        }

        // Compare essential properties
        for (let i = 0; i < this.mapData.length; i++) {
            const oldItem = this._lastMapData[i];
            const newItem = this.mapData[i];

            if (
                oldItem?.entityId !== newItem?.entityId ||
                oldItem?.coordinates?.[0] !== newItem?.coordinates?.[0] ||
                oldItem?.coordinates?.[1] !== newItem?.coordinates?.[1] ||
                oldItem?.totalAlertCount !== newItem?.totalAlertCount ||
                oldItem?.apiType !== newItem?.apiType
            ) {
                return true;
            }
        }

        return false;
    }

    // 9. ADD NEW METHOD: Efficiently update markers without recreating everything
    private _updateMarkersEfficiently() {
        if (!this._markerCluster) return;

        const currentIds = new Set(this.mapData.map(item => item.entityId));
        const previousIds = new Set(this._lastMapData.map(item => item.entityId));

        // ✅ Remove markers that no longer exist
        const markersToRemove: L.Marker[] = [];
        this._markersCache.forEach((marker, entityId) => {
            if (!currentIds.has(entityId)) {
                markersToRemove.push(marker);
                this._markersCache.delete(entityId);
            }
        });
        markersToRemove.forEach(marker => {
            this._markerCluster.removeLayer(marker);
        });

        // ✅ Add or update markers
        this.mapData.forEach(item => {
            if (!item?.coordinates) return;

            const entityId = item.entityId;
            const existingMarker = this._markersCache.get(entityId);

            if (!existingMarker) {
                // ✅ Add new marker
                const marker = this._createMarkerForItem(item);
                if (marker) {
                    this._markerCluster.addLayer(marker);
                    this._markersCache.set(entityId, marker);
                }
            } else {
                // ✅ Update existing marker if needed
                const oldItem = this._lastMapData.find(i => i.entityId === entityId);
                if (this._shouldUpdateMarker(oldItem, item)) {
                    // Remove old and add new
                    this._markerCluster.removeLayer(existingMarker);
                    this._markersCache.delete(entityId);

                    const newMarker = this._createMarkerForItem(item);
                    if (newMarker) {
                        this._markerCluster.addLayer(newMarker);
                        this._markersCache.set(entityId, newMarker);
                    }
                }
            }
        });

        // ✅ Update map bounds if needed
        if (this.mapData.length && (this.mapData[0]?.apiType === 'default' || this.mapData[0]?.singleTracker)) {
            this._map.fitBounds(this._markerCluster.getBounds());
        }
    }

    // 10. ADD method to check if marker needs update
    private _shouldUpdateMarker(oldItem: any, newItem: any): boolean {
        if (!oldItem) return true;

        return (
            oldItem.coordinates?.[0] !== newItem.coordinates?.[0] ||
            oldItem.coordinates?.[1] !== newItem.coordinates?.[1] ||
            oldItem.totalAlertCount !== newItem.totalAlertCount ||
            oldItem.markerImg !== newItem.markerImg ||
            oldItem.trackerType !== newItem.trackerType
        );
    }

    // 11. ADD method to create individual marker
    private _createMarkerForItem(item: any): L.Marker | null {
        if (!item?.coordinates) return null;

        let popOverContent = this._leafletService.getOtherEntityPopOverTemplate(item);

        if (item.entityType === 'tracker') {
            if (this._leafletService.checkVehicleCategory(item.entityCategory)) {
                popOverContent = this._leafletService.getVehiclePopOverTemplate(item);
            } else if (item.trackerType === 'vehicleValidOps') {
                popOverContent = `<div>Loading...</div>`;
            }
        } else if (item?.entityType === 'site') {
            popOverContent = this._leafletService.getSitesPopOverTemplate(item);
        }

        let marker: L.Marker;

        if (item?.markerImg) {
            const { normalIcon, alertIcon, alertLocationIcon } = this._createMarkerIcons(item);

            // Handle alert location marker for alertAnalyze page
            if (this.pageType === 'alertAnalyze' && item?.alertCoordinate && alertLocationIcon) {
                const alertMarker = L.marker(item.alertCoordinate, { icon: alertLocationIcon });
                alertMarker.bindTooltip('Alert');
                this._markerCluster.addLayer(alertMarker);
            }

            marker = L.marker(item.coordinates, {
                icon: item?.totalAlertCount ? alertIcon : normalIcon,
            });

            // Store entityId for later reference
            (marker as any).options.entityId = item.entityId;
        } else {
            marker = L.marker(item.coordinates);
            (marker as any).options.entityId = item.entityId;
        }

        // Setup click handler for async popup content
        marker.on('click', async () => {
            if (item.trackerType === 'vehicleValidOps') {
                await this._handleVehicleValidOpsPopup(marker, item);
            }
            this._centerMapOnPopup(marker);
        });

        // Bind popup and tooltip
        const isVehicleValidOps = item.trackerType === 'vehicleValidOps';
        const entityData = this._service.getLeafletData()?.find(data => data.entityId === item.entityId);
        if (entityData) {
            const popOverContentToUse = isVehicleValidOps
                ? this._leafletService.getVehicleValidopsPopover(entityData)
                : popOverContent;

            if (item.singleTracker || item.geoPopUp) {
                marker.addTo(this._map)
                    .bindPopup(popOverContentToUse, { keepInView: true })
                    .openPopup();
                marker.fire('click');
            } else {
                marker.bindPopup(popOverContent, { keepInView: true });
            }

            marker.bindTooltip(item?.entityName);
        }


        // Handle site double-click
        if (item.entityType === 'site') {
            marker.on('dblclick', () => {
                sessionStorage.setItem('siteId', JSON.stringify(item.entityId || null));
                this._dataSer.broadcast('trackerSiteChanged', item);
            });
        }

        return marker;
    }

    // 12. ADD helper method to create marker icons
    private _createMarkerIcons(item: any): { normalIcon: L.Icon | L.DivIcon, alertIcon: L.Icon | L.DivIcon, alertLocationIcon?: L.DivIcon } {
        let normalIcon: L.Icon | L.DivIcon;
        let alertIcon: L.Icon | L.DivIcon;
        let alertLocationIcon: L.DivIcon | undefined;

        if (item?.trackerType === 'vehicleValidOps') {
            const widgetStatusColorConfig = this._service.getConfiguration('widgetStatusColor')?.widgetStatusColor;
            const markerBgColor = widgetStatusColorConfig?.find(
                list => list?.type === item?.['attribute.derived.vehicleState']
            )?.markerColor || '';

            const createVehicleMarker = (showShadow: boolean) =>
                L.divIcon({
                    html: `
          <div class="custom-marker-wrapper" style="background-color: ${markerBgColor}">
            <img src="${item.markerImg}" alt="Marker Image" />
            ${showShadow && this.pageType !== 'alertAnalyze'
                            ? '<div class="marker-shadow"><img src="assets/images/alertWave.svg" alt="alert wave" /></div>'
                            : ''}
          </div>
        `,
                    iconAnchor: [20, 20],
                    iconSize: [40, 40],
                    className: 'vot-custom-marker-class',
                });

            normalIcon = createVehicleMarker(false);
            alertIcon = createVehicleMarker(true);

            if (this.pageType === 'alertAnalyze') {
                const alertLocationIconBgColor = widgetStatusColorConfig?.find(
                    list => list?.type === item?.severity
                )?.markerColor || '';
                const alertMarkerWaveColor = widgetStatusColorConfig?.find(
                    list => list?.type === item?.severity
                )?.bgColor || '';

                alertLocationIcon = L.divIcon({
                    html: `
          <div class="custom-alert-marker-wrapper" style="background-color: ${alertLocationIconBgColor}">
            <img src="assets/images/warning.svg" alt="Marker Image" />
            <div class="alert-marker-shadow">
              <svg width="56" height="55" viewBox="0 0 56 55" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="27.6106" cy="27.0361" rx="27.6106" ry="27.0361" fill="${alertMarkerWaveColor}"/>
              </svg>
            </div>
          </div>
        `,
                    iconAnchor: [20, 20],
                    iconSize: [50, 50],
                    className: 'vot-custom-alert-marker-class',
                });
            }
        } else {
            normalIcon = L.icon({
                iconUrl: item?.markerImg,
                iconAnchor: [20, 20],
                iconSize: [40, 40],
            });

            alertIcon = L.icon({
                iconUrl: item?.markerImg,
                shadowUrl: 'assets/images/alertWave.svg',
                iconSize: [40, 40],
                iconAnchor: [20, 20],
                shadowAnchor: [30, 30],
                shadowSize: [60, 60],
            });
        }

        return { normalIcon, alertIcon, alertLocationIcon };
    }

    // 13. ADD helper method for vehicle valid ops popup
    private async _handleVehicleValidOpsPopup(marker: L.Marker, item: any) {
        try {
            const vehicleData = this._service.getLeafletData()?.find(data => data.entityId === item.entityId);
            if (vehicleData) {
                const latitude = vehicleData?.coordinates?.[0] ?? vehicleData['attribute.canbs4.Latitude'];
                const longitude = vehicleData?.coordinates?.[1] ?? vehicleData['attribute.canbs4.Longitude'];

                if (latitude !== null && longitude !== null) {
                    const locationDetails = await this.getLocationFromCoordinates(latitude, longitude);
                    vehicleData.mapLocation = locationDetails;
                }

                if (vehicleData?.entityId) {
                    const aggrDetails = await this.getAggregateDetails(vehicleData?.entityId);
                    vehicleData.aggrDetails = {};
                    vehicleData.aggrDetails.totalDistance = aggrDetails?.[0]?.totalDistance;
                    vehicleData.aggrDetails.totalEnginehours = aggrDetails?.[0]?.totalDistance;
                    vehicleData.aggrDetails.trimmedTotalDistance = Number(aggrDetails?.[0]?.totalDistance)?.toFixed(1);
                    vehicleData.aggrDetails.totalEnginehoursTrimmed = Number(aggrDetails?.[0]?.totalEnginehours)?.toFixed(1);
                }

                const updatedPopOverContent = this._leafletService.getVehicleValidopsPopover(vehicleData);
                marker.bindPopup(updatedPopOverContent, { keepInView: true }).openPopup();

            }
        } catch (error) {
            console.error('Error fetching location:', error);
        }
    }

    // 14. ADD helper method to center map on popup
    private _centerMapOnPopup(marker: L.Marker) {
        const popup = marker.getPopup();
        if (popup && popup?.getLatLng()) {
            const anchorLatLng = popup.getLatLng();
            const anchorPoint = this._map.latLngToContainerPoint(anchorLatLng);
            const mapSize = this._map.getSize();
            const targetY = mapSize.y * 0.7;
            const dy = anchorPoint.y - targetY;
            this._map.panBy([0, dy]);
        }
    }

    // 15. ADD method for trip playback handling
    private _handleTripPlayback() {
        this._map.on('movestart', () => (this.isUserInteracting = true));
        this._map.on('moveend', () => {
            setTimeout(() => (this.isUserInteracting = false), 5000);
        });

        if (!this.showAiAssist) {
            this.traversePoints();
        } else {
            this._map.fitBounds(this._initialBounds);
            this.addTripAssisPoints();
        }
    }

    // 16. ADD cleanup method
    private _cleanupMap() {
        if (this._map) {
            this._map.remove();
            this._map = null;
        }
        this._markerCluster = null;
        this._markersCache.clear();
        this._isMapInitialized = false;
    }

    // 17. MODIFY _leafletMarkerCluster - Use caching
    private _leafletMarkerCluster() {
        if (!this._markerList.some((elem: any) => elem?.coordinates)) {
            return;
        }

        this._markerList = this._markerList?.filter((item: any) => item?.coordinates);

        // ✅ Only create cluster if it doesn't exist
        if (!this._markerCluster) {
            this._markerCluster = L.markerClusterGroup({
                iconCreateFunction: (cluster) => {
                    return this._createClusterIcon(cluster);
                },
            });
        } else {
            // ✅ Clear only if needed
            this._markerCluster.clearLayers();
            this._markersCache.clear();
        }

        // ✅ Use the new efficient marker creation
        for (const item of this._markerList) {
            const marker = this._createMarkerForItem(item);
            if (marker) {
                this._markerCluster.addLayer(marker);
                this._markersCache.set(item.entityId, marker);
            }
        }

        this._map.addLayer(this._markerCluster);

        if (this._markerList?.length && (this._markerList[0]?.apiType === 'default' || this._markerList[0]?.singleTracker)) {
            this._map.fitBounds(this._markerCluster?.getBounds());
        }

    }

    // 18. ADD helper for cluster icon creation
    private _createClusterIcon(cluster: any): L.DivIcon {
        const childClustersList = cluster.getAllChildMarkers();
        const hasAlerts = childClustersList.some((item: any) => {
            return this._markerList
                .filter((item: any) => item?.totalAlertCount)
                .some((data: any) => {
                    return item?._latlng?.lat === data?.coordinates[0] &&
                        item?._latlng?.lng === data?.coordinates[1];
                });
        });

        if (hasAlerts) {
            return L.divIcon({
                html: `<div class="d-flex align-items-center justify-content-center position-relative">
              <div class="position-absolute"><img src="../../../../assets/images/alertWave.svg" class="map-alert-image"></div>
              <div class="map-warning-circle">${cluster.getChildCount()}</div>
            </div>`,
                className: '',
                iconSize: L.point(40, 40, true),
            });
        } else {
            return L.divIcon({
                html: `<div class="map-normal-circle"><span style="color:#fff">${cluster.getChildCount()}</span></div>`,
                className: '',
                iconSize: L.point(40, 40, true),
            });
        }
    }

    /**
     * Sets zoom level
     * @returns
     */
    private _setZoomLevel() {
        switch (this.pageType) {
            case 'tracker':
                return this._service.getConfiguration(
                    'trackerDefaultLocation'
                )
                    ? this._service.getConfiguration('trackerDefaultLocation')['siteSpecificZoom']
                    : [];
            case 'allsites':
                return this._service.getConfiguration(
                    'trackerDefaultLocation'
                )
                    ? this._service.getConfiguration('trackerDefaultLocation')['allSitesZoom']
                    : [];
            default:
                return 13;
        }
    }

    /**
     * Initializes leaflet map component
     */
    private _initialize() {
        // L.setOptions(false);
        const osm = L.tileLayer(
            'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            {
                attribution:
                    '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            }
        );
        const gMap = L.tileLayer(
            'http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
            {
                maxZoom: 20,
                subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
            }
        );
        // Add HERE Maps tile layer
        const hereMap = L.tileLayer(
            `${ApiConstants?.HERE_TILE_URL}?apiKey=${ApiConstants?.HERE_API_KEY}&lang=en`,
            {
                attribution: '&copy; HERE Maps',
                maxZoom: 20,
                minZoom: 1,
            }
        );
        const layerMap = {
            gMap,
            osm,
            hereMap,
        };
        const layers = this._tenantConfig?.trackerMapLayers
            ? this._tenantConfig?.trackerMapLayers?.map(
                (layerName) => layerMap[layerName]
            )
            : [gMap, osm];
        this._map = L.map('map', { layers: layers }).setView(
            this.defaultLocation,
            this._setZoomLevel(),
            {
                animate: true,
            }
        );

        const baseLayerMap = {
            GoogleMap: gMap,
            OpenStreetMap: osm,
            'HERE Maps': hereMap,
        };

        const baseMaps = this._tenantConfig?.trackerBaseMaps
            ? Object.entries(this._tenantConfig?.trackerBaseMaps)?.reduce(
                (baseMap, [key, _]) => {
                    baseMap[key] = baseLayerMap[key];
                    return baseMap;
                },
                {} as Record<string, any>
            )
            : {
                GoogleMap: gMap,
                OpenStreetMap: osm,
            };

        if (
            this.popOver ||
            this.pageType === 'allsites' ||
            this.pageType === 'zones'
        ) {
            L.control.layers(baseMaps).addTo(this._map);
        }
        if (this.pageType === 'tracker' && Object.entries(baseMaps)?.length > 1) {
            L.control
                .layers(baseMaps, null, { position: 'bottomleft' })
                ?.addTo(this._map);
        }
        this._map.on('pm:create', (e) => {
            e.layer.options.pmIgnore = false;
            const layer = L.geoJson().addTo(this._map);
            if (this._map?.pm && typeof this._map?.pm?.reInitLayer === 'function') {
                this._map?.pm?.reInitLayer(layer);
            }
            if (e?.shape === 'Polygon') {
                this._polygonArray = e?.layer?.getLatLngs().length
                    ? e?.layer?.getLatLngs()[0]
                    : [];
                this.drawZoneAction.emit(this._polygonArray);
            } else {
                this._circleArray =
                    e?.layer?._latlng && e?.layer?._mRadius
                        ? [
                            {
                                latitude: e?.layer?._latlng?.lat,
                                longitude: e?.layer?._latlng?.lng,
                            },
                            { radius: e.layer.getRadius() },
                        ]
                        : [];
                this.drawZoneAction.emit(this._circleArray);
            }
        });
        this._map.on('pm:drawend', (e) => {
            if (this.popOver) {
                this._dataSer.broadcast('showZoneForm', true);
            }
            this._map?.pm?.addControls({
                removalMode: true,
                drawPolygon: false,
                drawCircle: false,
            });
        });
        this._map.on('pm:remove', (e) => {
            if (this.zoneType !== 'edit') {
                if (this.pageType === 'zones') {
                    if (this.mapData?.length > this.zoneLength) {
                        this.mapData?.pop();
                    }
                    this._existingZoneData = this.mapData;
                }
                let coordinatesToRemove: [number, number][];
                if (e.layer instanceof L.Circle) {
                    coordinatesToRemove = [
                        [e?.layer?._latlng?.lat, e?.layer?._latlng?.lng],
                    ];
                } else if (e.layer instanceof L.Polygon) {
                    coordinatesToRemove = e?.layer?._latlngs
                        ?.flat()
                        ?.map((latlng: L.LatLng) => [latlng?.lat, latlng?.lng]);
                }
                const canRemove = !this._isCoordinatesInExistingZones(
                    coordinatesToRemove,
                    this._existingZoneData
                );
                if (!canRemove) {
                    this._map?.addLayer(e?.layer);
                } else {
                    if (this.popOver) {
                        this._dataSer.broadcast('showZoneForm', false);
                    }
                    this._map?.pm?.addControls({
                        removalMode: false,
                        drawPolygon: true,
                        drawCircle: this.hierarchy ? false : true,
                    });
                    this.removeZoneAction.emit('removed');
                }
            } else {
                if (this.mapData?.length > this.zoneLength) {
                    this.mapData?.pop();
                }
                this._map?.pm?.addControls({
                    removalMode: false,
                    drawPolygon: true,
                    drawCircle: this.hierarchy ? false : true,
                });
                this.removeZoneAction.emit('removed');
            }
        });
        if (
            (this.pageType === 'tracker' && this._tenantConfig?.tenant === 'VOT') ||
            this.styleType === 'full'
        ) {
            this._map?.zoomControl?.remove();
            L.control.zoom({ position: 'bottomright' }).addTo(this._map);
        }
        if (this.pageType === 'profile') {
            // this.leafletPloygon();
            this._map?.zoomControl?.remove();
            this._leafletLine();
            if (!this.showAiAssist) {
                this.traversePoints();
            } else {
                this.addTripAssisPoints();
            }
            if (this.popOver && this.showControls) {
                this._map.pm.addControls({
                    position: 'topleft',
                    drawCircle: true,
                    drawMarker: false,
                    drawCircleMarker: false,
                    drawPolyline: false,
                    drawRectangle: false,
                    drawPolygon: true,
                    drawText: false,
                    editMode: false,
                    dragMode: false,
                    cutPolygon: false,
                    removalMode: false,
                    rotateMode: false,
                });
            }
        } else {
            if (this.pageType === 'zones') {
                // this.imageOverlay();
                if (
                    this.zoneType === 'edit' ||
                    this.mapData?.length > this.zoneLength
                ) {
                    this._map.pm.addControls({
                        position: 'topleft',
                        drawCircle: false,
                        drawMarker: false,
                        drawCircleMarker: false,
                        drawPolyline: false,
                        drawRectangle: false,
                        drawPolygon: false,
                        drawText: false,
                        editMode: false,
                        dragMode: false,
                        cutPolygon: false,
                        removalMode: true,
                        rotateMode: false,
                    });
                    this._map.fireEvent('pm:drawend', { shape: 'Polygon' });
                } else {
                    this._map.pm.addControls({
                        position: 'topleft',
                        drawCircle: this.hierarchy ? false : true,
                        drawMarker: false,
                        drawCircleMarker: false,
                        drawPolyline: false,
                        drawRectangle: false,
                        drawPolygon: true,
                        drawText: false,
                        editMode: false,
                        dragMode: false,
                        cutPolygon: false,
                        removalMode: false,
                        rotateMode: false,
                    });
                }
                this._mapSearch();
                this._leafletPloygon();
            }
            this._leafletMarkerCluster();
        }
    }

    /**
     * Determines whether coordinates in existing zones is
     * @param coordinates
     * @param existingZones
     * @returns true if coordinates in existing zones
     */
    private _isCoordinatesInExistingZones(
        coordinates: any[],
        existingZones: any[]
    ): boolean {
        return existingZones?.some((zone) => {
            const zoneCoords = this.popOver ? zone?.details : zone?.polygonData;
            if (coordinates?.length > 1) {
                if (zoneCoords?.length !== coordinates?.length) {
                    return false;
                }
            }
            return zoneCoords.every(
                (coord: [number, number], index: number) =>
                    coord[0] === coordinates[index][0] &&
                    coord[1] === coordinates[index][1]
            );
        });
    }

    /**
     * Gets existing zone details geo trace
     * @param [zone]
     */
    private _getExistingZoneDetailsGeoTrace(zone: any = []) {
        this._selectedZoneName = zone?.name;
        if (zone?.details?.[1]?.radius) {
            this._existingCircleArray.push(zone?.details);
        } else {
            this._existingPolygonArray.push(zone?.details);
        }
        this._leafletCirclePolygon();
    }

    /**
     * Adds trip assis points
     */
    addTripAssisPoints() {
        this.tripAssistPlots?.forEach(element => {
            L.marker(element, {
                icon: L.icon({
                    iconUrl: 'assets/images/AiAssistTrip.svg',
                    iconSize: [40, 40],
                    iconAnchor: [20, 20],
                    shadowAnchor: [30, 30],
                    shadowSize: [60, 60],
                })
            }).addTo(this._map);
        });
    }

    /**
     * Routing marker
     */
    private _routingMarker() {
        const routeMarker = L.marker([8.535401, 76.880777]).addTo(this._map);
        const interactionPopup = `<div>
          <div>
            The leaflet map pop over for navigation.
          </div>
          <button class="find">Find route</button> &nbsp;&nbsp;<button class="cancel">Cancel Route</button>
        </div>`;
        routeMarker.bindPopup(interactionPopup).on('popupopen', (elem) => {
            const popUp = elem?.target?.getPopup()?.getElement();
            popUp.querySelector('.find').addEventListener('click', () => {
                this._showRoute();
            });
            popUp.querySelector('.cancel').addEventListener('click', () => {
                this._vehicleRoute.remove();
            });
        });
    }

    /**
     * Shows route
     */
    private _showRoute() {
        this._vehicleRoute = L.Routing.control({
            waypoints: [L.latLng(8.893212, 76.614143), L.latLng(8.524139, 76.936638)],
        }).addTo(this._map);
    }

    /**
     * Leaflets marker
     * @param markerList
     */
    private _leafletMarker(markerList: any) {
        let popOverContent = null;
        markerList.forEach((item: any) => {
            let marker = null;
            if (item?.markerImg) {
                const normalMarkerIcon = L.icon({
                    iconUrl: item?.markerImg,
                    iconAnchor: [20, 0],
                });
                const alertMarkerIcon = L.icon({
                    iconUrl: 'assets/images/truck-solid.svg',
                    shadowUrl: 'assets/images/waveAnim.gif',
                    shadowAnchor: [37, 5],
                    iconAnchor: [20, 0],
                });
                marker = L.marker(item?.coordinates, {
                    icon: item?.totalAlertCount ? alertMarkerIcon : normalMarkerIcon,
                }).addTo(this._map);
            } else {
                marker = L.marker(item?.coordinates).addTo(this._map);
            }
            if (item.entityType === 'tracker') {
                popOverContent =
                    '<div class="d-grid form-gap w-100 grid-column"><img src="../../../../assets/images/bellIcon.svg" style="pointer-events:none;"' +
                    'title="Alerts" class="h-30 space-after-icon" alt="Alerts"><a style="display:flex;" href="/#/events/alerts/' +
                    item.entityId +
                    '">' +
                    '<div class="d-flex align-items-center"><strong class="ft-20">' +
                    item.totalAlertCount +
                    '</strong></a></div><div class="d-flex align-items-center" style="color:red">' +
                    item['alertName'] +
                    '</div><a class="nav-link" href="/#/vehicles/profile/' +
                    item.entityId +
                    '">' +
                    '<img src="../../../../assets/images/goto-profile.svg" title="Go to profile" class="h-30 space-after-icon" alt="goto"></a></div>' +
                    '<div class="d-grid grid-column"><div class="d-flex flex-column justify-content-center align-items-center w-100 l-space-between-containers">' +
                    '<div class="position-relative l-space-between-containers"><img src="' +
                    item['image'] +
                    '"' +
                    'class="card-img-top rounded-circle image-container" alt="vehicle"></div>' +
                    '</div><div style="padding: 15px;"><div class="d-flex flex-column justify-content-center w-100"><div class="d-flex"></div>' +
                    '<h5 class="l-space-between-containers"><strong>' +
                    item['entityName'] +
                    '</strong></h5></div>' +
                    '<div class="d-flex w-100"><div>' +
                    'Skoda' +
                    ' - ' +
                    'Octavia' +
                    '</div></div></div></div><div style="display:flex;justify-content: space-between;"><div><button mat-button disabled><img src="../../assets/images/truck-solid.svg"' +
                    'class="w-20 opacity3 space-after-icon2" title="' +
                    'Skoda' +
                    '" alt="Make"></button><button mat-button disabled><img src="../../assets/images/petrol-engine.svg"' +
                    'class="w-20 opacity3 space-after-icon2" title="1000bhp"alt="Make"></button><button mat-button disabled><img src="../../assets/images/car-battery-solid.svg"' +
                    'class="w-20 opacity3 space-after-icon2" title="Battery" alt="Battery"></button><button mat-button disabled><img src="../../assets/images/fuel.svg"' +
                    'class="w-20 opacity3 space-after-icon2" title="Fuel" alt="Make"></button></div><div style="display:flex;"><br><span class="center">' +
                    '97' +
                    '<img src="../../../../assets/images/star-solid.svg"' +
                    'class="w-20 opacity3 space-after-icon star" title="Vehicle Score" alt="Make"></span></div></div>';
            } else if (item.entityType === 'site') {
                popOverContent =
                    '<div class="d-grid form-gap w-100 grid-column"><img src="../../../../assets/images/bellIcon.svg" style="pointer-events:none;"' +
                    'title="Alerts" class="h-30 space-after-icon" alt="Alerts"><a style="display:flex;" href="/#/events/alerts/' +
                    item.entityId +
                    '">' +
                    '<div class="d-flex align-items-center"><strong class="ft-20">' +
                    item.totalAlertCount +
                    '</strong></a></div><div class="d-flex align-items-center" style="color:red">' +
                    item['alertName'] +
                    '</div><a class="nav-link" href="/#/plants/profile/' +
                    item.entityId +
                    '">' +
                    '<img src="../../../../assets/images/goto-profile.svg" title="Go to profile" class="h-30 space-after-icon" alt="goto"></a></div>' +
                    '<div class="d-grid grid-column"><div class="d-flex flex-column justify-content-center align-items-center w-100 l-space-between-containers">' +
                    '<div class="position-relative l-space-between-containers"><img src="' +
                    item['image'] +
                    '"' +
                    'class="card-img-top rounded-circle image-container" alt="vehicle"></div>' +
                    '</div><div style="padding: 15px;"><div class="d-flex flex-column justify-content-center w-100"><div class="d-flex"></div>' +
                    '<h5 class="l-space-between-containers"><strong>' +
                    item['entityName'] +
                    '</strong></h5></div>' +
                    '<div class="d-flex w-100"></div></div></div></div>';
            }
            if (item?.singleTracker) {
                marker.bindPopup(popOverContent).openPopup();
                marker.bindTooltip(item?.entityName);
            } else if (!item?.singleTracker) {
                marker.bindPopup(popOverContent);
                marker.bindTooltip(item?.entityName);
            }
        });
    }

    /**
     * Maps search
     */
    private _mapSearch() {
        L.Control.geocoder({
            collapsed: true,
            position: 'bottomright',
        }).addTo(this._map);
    }

    /**
     * Leaflets ploygon
     */
    private _leafletPloygon() {
        if (this._markerList?.some((elem: any) => elem?.polygonData)) {
            this.zoneType === 'edit' &&
                (this._markerList?.find(
                    (detail) => detail?.name === this.zoneData?.item?.name
                )?.polygonData?.length > 0 ||
                    this.mapData?.length > this.zoneLength)
                ? this._map?.pm?.addControls({
                    removalMode: true,
                    drawPolygon: false,
                    drawCircle: false,
                })
                : this._map?.pm?.addControls({
                    removalMode: false,
                    drawPolygon: true,
                    drawCircle: this.hierarchy ? false : true,
                });
            if (this.zoneType === 'add') {
                let polygon, circleLayer;
                this._markerList?.forEach((elem: any) => {
                    if (elem?.polygonData) {
                        if (!elem?.radius) {
                            polygon = L.polygon(elem?.polygonData, {
                                color: 'blue',
                                fillColor: '#fd7e14',
                            });
                            this.pageType === 'profile'
                                ? polygon?.bindTooltip(elem?.entityName).addTo(this._map)
                                : polygon.bindTooltip(elem?.name).addTo(this._map);
                        } else {
                            circleLayer = L.circle(
                                [elem?.polygonData?.[0]?.[0], elem?.polygonData?.[0]?.[1]],
                                {
                                    color: 'blue',
                                    fillColor: '#fd7e14',
                                    radius: elem?.radius,
                                }
                            );
                            circleLayer.bindTooltip(elem?.name).addTo(this._map);
                        }
                    }
                });
                if (polygon) this._map.fitBounds(polygon?.getBounds());
                if (circleLayer) this._map.fitBounds(circleLayer?.getBounds());
            } else if (this.zoneType === 'edit' || this.zoneType === 'view') {
                let editMarkerlist = !this._markerList[this._markerList?.length - 1]
                    ?.name
                    ? this._markerList[this._markerList?.length - 1]
                    : this._markerList?.find(
                        (detail) => detail?.name === this.zoneData?.item?.name
                    );
                if (
                    editMarkerlist?.polygonData &&
                    editMarkerlist?.polygonData?.length > 0
                ) {
                    if (!editMarkerlist?.radius) {
                        const polygon = L.polygon(editMarkerlist?.polygonData, {
                            color: 'blue',
                            fillColor: '#fd7e14',
                        });
                        this.pageType === 'profile'
                            ? polygon
                                ?.bindTooltip(editMarkerlist.entityName)
                                ?.addTo(this._map)
                            : polygon?.bindTooltip(editMarkerlist.name)?.addTo(this._map);
                        this._map.fitBounds(polygon?.getBounds());
                    } else {
                        const circleLayer = L.circle(
                            [
                                editMarkerlist?.polygonData?.[0]?.[0],
                                editMarkerlist?.polygonData?.[0]?.[1],
                            ],
                            {
                                fillOpacity: 0.5,
                                radius: editMarkerlist?.radius?.[0]
                                    ? editMarkerlist?.radius?.[0]
                                    : editMarkerlist?.radius,
                            }
                        );
                        circleLayer.bindTooltip(editMarkerlist?.name).addTo(this._map);
                        this._map.fitBounds(circleLayer?.getBounds());
                    }
                }
            }
        } else if (this.hierarchy && this.zoneType === 'add') {
            this._map?.pm?.addControls({
                removalMode: false,
                drawPolygon: true,
                drawCircle: false,
            });
        } else if (this.pageType === 'profile') {
            L.polygon(this._markerList?.[0]?.polygonData, {
                color: 'blue',
                fillColor: '#fd7e14',
            });
        }
        if (this.zoneType === 'view') {
            this._map.pm.addControls({
                removalMode: false,
                drawPolygon: false,
                drawCircle: false,
            });
        }
        if (this.pageType === 'tracker') {
            let polygon, circleLayer;
            if (this._showZones) {
                this.trackerZoneData?.forEach((elem: any) => {
                    this._trackerzoneNameArray?.push(elem?.name);
                    if (elem?.polygonData) {
                        if (!elem?.polygonData?.[1]?.radius) {
                            polygon = L.polygon(elem?.polygonData, {
                                color: 'blue',
                                fillColor: '#fd7e14',
                            });
                            polygon.bindTooltip(elem?.name).addTo(this._map);
                            this._polygonLayers?.set(elem?.name, polygon);
                        } else {
                            circleLayer = L.circle(
                                [
                                    elem?.polygonData?.[0]?.center?.lat,
                                    elem?.polygonData?.[0]?.center?.lng,
                                ],
                                {
                                    color: 'blue',
                                    fillColor: '#fd7e14',
                                    radius: elem?.polygonData?.[1]?.radius,
                                }
                            );
                            circleLayer.bindTooltip(elem?.name).addTo(this._map);
                            this._circleLayers.set(elem?.name, circleLayer);
                        }
                    }
                });
            }
            if (polygon) this._map.fitBounds(polygon?.getBounds());
            if (circleLayer) this._map.fitBounds(circleLayer?.getBounds());
            if (!this._showZones && this._trackerzoneNameArray?.length > 0) {
                this._trackerzoneNameArray?.forEach((elem: string) => {
                    if (this._circleLayers?.has(elem)) {
                        this._map?.removeLayer(this._circleLayers?.get(elem));
                        this._circleLayers?.delete(elem);
                    } else if (this._polygonLayers?.has(elem)) {
                        this._map?.removeLayer(this._polygonLayers?.get(elem));
                        this._polygonLayers?.delete(elem);
                    }
                });
            }
        }
    }

    private traversePoints() {
        const currentCoordinate: L.LatLngExpression =
            this._generalService?.isNumber(
                this._markerList?.[0]?.currentCoordinate?.[0]
            ) &&
                this._generalService?.isNumber(
                    this._markerList?.[0]?.currentCoordinate?.[1]
                )
                ? this._markerList?.[0]?.currentCoordinate
                : null;

        const nextCoordinate =
            this._generalService?.isNumber(
                this._markerList?.[0]?.upcomingCoordinate?.[0]
            ) &&
                this._generalService?.isNumber(
                    this._markerList?.[0]?.upcomingCoordinate?.[1]
                )
                ? this._markerList?.[0]?.upcomingCoordinate
                : null;

        if (!currentCoordinate) return; // Exit if no valid coordinates

        // Only calculate a new angle if we have different coordinates
        let angle = this.lastValidAngle; // Default to using last valid angle

        // Check if we have both coordinates and they're different
        if (
            nextCoordinate &&
            this.areCoordinatesDifferent(currentCoordinate, nextCoordinate)
        ) {
            angle = this.calculateBearing(currentCoordinate, nextCoordinate);
            this.lastValidAngle = angle;
        } else {
            console.log('Using previous angle:', angle);
        }
        // Calculate the angle between current and next coordinates

        const customIcon = L.divIcon({
            className: 'custom-marker', // CSS class
            html: `<div class="marker-container" style="position: relative; width: 100%; height: 100%;">
      <div class="marker-rotation" style="transform: rotate(${angle}deg); transform-origin: center center; position: absolute; width: 100%; height: 100%;">
      <svg width="36" height="32" viewBox="0 0 36 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g filter="url(#filter0_d_27338_68581)" transform="rotate(-90, 18, 16)">
          <path d="M28.8993 5.74152C29.9842 4.90319 29.5537 3.17684 28.2136 2.99121L7.19947 0.0805532L6.55694 1.20339L12.3322 8.73321L10.802 18.2293L11.8884 18.8868L28.8993 5.74152Z" fill="#FDFDFD"/>
          <path d="M13.0874 8.84504C13.1212 8.63481 13.068 8.42118 12.9401 8.25448L7.47607 1.13036L7.59825 0.916843L28.0975 3.75618C28.7676 3.84899 28.9828 4.71217 28.4403 5.13134L11.8462 17.9545L11.6396 17.8295L13.0874 8.84504Z" stroke="url(#paint0_linear_27338_68581)" stroke-width="1.5373" stroke-linejoin="round"/>
        </g>
        <defs>
          <filter id="filter0_d_27338_68581" x="0.405485" y="0.0805664" width="35.2593" height="31.1046" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
            <feFlood flood-opacity="0" result="BackgroundImageFix"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dy="6.1492"/>
            <feGaussianBlur stdDeviation="3.0746"/>
            <feComposite in2="hardAlpha" operator="out"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_27338_68581"/>
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_27338_68581" result="shape"/>
          </filter>
          <linearGradient id="paint0_linear_27338_68581" x1="17.9019" y1="-2.80005" x2="22.9385" y2="15.9126" gradientUnits="userSpaceOnUse">
            <stop stop-color="#86AAD8"/>
            <stop offset="1" stop-color="#173C6D"/>
          </linearGradient>
        </defs>
      </svg>
      </div>
      </div>`,
            iconSize: [40, 40], // Adjust size
            iconAnchor: [15, 10], // Positioning
        });

        let alertLocationMarker;
        let alertMarker = null;
        if (this._markerList?.[0]?.alertCoordinate) {
            alertLocationMarker = L.divIcon({
                html: `
        <svg width="27" height="27" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg" class="custom-alert-icon">
          <g filter="url(#filter0_d_27394_37858)">
            <circle cx="11" cy="11" r="10" fill="${this._markerList?.[0]?.alertSeverity}" />
            <circle cx="11" cy="11" r="10" stroke="white" />
          </g>
          <mask id="mask0_27394_37858" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="6" y="5" width="11" height="11">
            <rect x="6" y="5" width="10.5664" height="10.5188" fill="#D9D9D9"/>
          </mask>
          <g mask="url(#mask0_27394_37858)">
            <path d="M7.51394 13.9846C7.43932 13.9846 7.37229 13.9664 7.31285 13.9301C7.25341 13.8938 7.20722 13.8459 7.17428 13.7865C7.13993 13.7274 7.12104 13.6635 7.11759 13.5947C7.11414 13.5259 7.13278 13.4577 7.17351 13.3903L10.9425 6.90892C10.9833 6.84142 11.0343 6.79153 11.0955 6.75925C11.1568 6.72696 11.2213 6.71082 11.289 6.71082C11.3567 6.71082 11.4212 6.72696 11.4825 6.75925C11.5437 6.79153 11.5947 6.84142 11.6355 6.90892L15.4045 13.3903C15.4452 13.4577 15.4639 13.5259 15.4604 13.5947C15.457 13.6635 15.4381 13.7274 15.4037 13.7865C15.3708 13.8459 15.3246 13.8938 15.2652 13.9301C15.2057 13.9664 15.1387 13.9846 15.0641 13.9846H7.51394ZM11.289 12.8046C11.3898 12.8046 11.4742 12.7707 11.5424 12.7028C11.6105 12.6349 11.6446 12.5509 11.6446 12.4506C11.6446 12.3503 11.6105 12.2662 11.5424 12.1983C11.4742 12.1305 11.3898 12.0965 11.289 12.0965C11.1883 12.0965 11.1038 12.1305 11.0356 12.1983C10.9675 12.2662 10.9334 12.3503 10.9334 12.4506C10.9334 12.5509 10.9675 12.6349 11.0356 12.7028C11.1038 12.7707 11.1883 12.8046 11.289 12.8046ZM11.2891 11.6583C11.3827 11.6583 11.4612 11.6268 11.5243 11.5638C11.5876 11.5008 11.6192 11.4227 11.6192 11.3296V9.79556C11.6192 9.70243 11.5875 9.62438 11.5242 9.56141C11.4609 9.49837 11.3825 9.46685 11.2889 9.46685C11.1953 9.46685 11.1169 9.49837 11.0537 9.56141C10.9904 9.62438 10.9588 9.70243 10.9588 9.79556V11.3296C10.9588 11.4227 10.9905 11.5008 11.0538 11.5638C11.1171 11.6268 11.1956 11.6583 11.2891 11.6583Z" fill="white"/>
          </g>
          <defs>
            <filter id="filter0_d_27394_37858" x="0.5" y="0.5" width="26" height="26" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
              <feFlood flood-opacity="0" result="BackgroundImageFix"/>
              <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
              <feMorphology radius="2" operator="erode" in="SourceAlpha" result="effect1_dropShadow_27394_37858"/>
              <feOffset dx="4" dy="4"/>
              <feGaussianBlur stdDeviation="1.5"/>
              <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.5 0"/>
              <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_27394_37858"/>
              <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_27394_37858" result="shape"/>
            </filter>
          </defs>
        </svg>  
        `,
                iconAnchor: [20, 20],
                iconSize: [50, 50],
                className: 'vot-custom-alert-marker-class',
            });
            alertMarker = L.marker(this._markerList?.[0]?.alertCoordinate, {
                icon: alertLocationMarker,
            }).addTo(this._map);
            alertMarker.bindTooltip('Alert');
        }

        if (!this.traverseMarker) {
            this.traverseMarker = L.marker(currentCoordinate, {
                icon: customIcon,
            }).addTo(this._map);
        } else {
            this.traverseMarker.setLatLng(currentCoordinate);
            this.traverseMarker.setIcon(customIcon);
        }

        let distance;
        let zoomLevel;
        if (nextCoordinate) {
            distance = this._calculateDistance(currentCoordinate, nextCoordinate);
            // Dynamically adjust zoom level
            zoomLevel = this._calculateZoomLevel(distance);
        }

        // Fly to the point with optimized parameters
        if (!this.isUserInteracting) {
            this._map.invalidateSize();
            setTimeout(() => {
                this._map.flyTo(currentCoordinate, zoomLevel ?? 16, {
                    animate: true,
                    duration: 0.7,
                    easeLinearity: 0.5,
                    noMoveStart: true,
                    padding: { top: 50, bottom: 50, left: 50, right: 50 },
                });
            }, 50);
        }

        // Smoothly pan and zoom the map to the new location
        // Re-center only if auto-traversing is active
        // if (!this.isUserInteracting) {
        //   this._map.flyTo(currentCoordinate, 16, { animate: true, duration: 0.8 });
        // }
    }

    private _calculateDistance(coord1, coord2) {
        const R = 6371; // Earth's radius in kilometers
        const [lat1, lon1] = coord1;
        const [lat2, lon2] = coord2;

        const dLat = ((lat2 - lat1) * Math.PI) / 180;
        const dLon = ((lon2 - lon1) * Math.PI) / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos((lat1 * Math.PI) / 180) *
            Math.cos((lat2 * Math.PI) / 180) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c; // Distance in kilometers
    }

    // Calculate zoom level based on distance
    private _calculateZoomLevel(distance) {
        // Adjust zoom dynamically based on distance
        if (distance < 100) return 16; // Close points
        if (distance < 500) return 10; // Medium distance
        if (distance < 1000) return 7; // Long distance
        return 3; // Very long distance
    }

    private areCoordinatesDifferent(
        coord1: L.LatLngExpression,
        coord2: L.LatLngExpression
    ): boolean {
        const latlng1 = L.latLng(coord1);
        const latlng2 = L.latLng(coord2);

        // Use a small threshold to account for floating point precision
        const threshold = 0.0000001;

        return (
            Math.abs(latlng1.lat - latlng2.lat) > threshold ||
            Math.abs(latlng1.lng - latlng2.lng) > threshold
        );
    }

    private calculateBearing(
        current: L.LatLngExpression,
        next: L.LatLngExpression
    ): number {
        // Convert to actual LatLng objects if they aren't already
        const currentLatLng = L.latLng(current);
        const nextLatLng = L.latLng(next);

        // Get coordinates in radians
        const startLat = (currentLatLng.lat * Math.PI) / 180;
        const startLng = (currentLatLng.lng * Math.PI) / 180;
        const destLat = (nextLatLng.lat * Math.PI) / 180;
        const destLng = (nextLatLng.lng * Math.PI) / 180;

        // Calculate bearing
        const y = Math.sin(destLng - startLng) * Math.cos(destLat);
        const x =
            Math.cos(startLat) * Math.sin(destLat) -
            Math.sin(startLat) * Math.cos(destLat) * Math.cos(destLng - startLng);
        let bearing = (Math.atan2(y, x) * 180) / Math.PI;

        // Normalize to 0-360
        bearing = (bearing + 360) % 360;

        return bearing;
    }

    /**
     * Leaflets line
     */
    private _leafletLine(dataPoints?: any) {
        (dataPoints ?? this._markerList)?.forEach((elem: any) => {
            const mapCoordinates = elem?.polygonData?.filter(
                (mapCoordinate) =>
                    this._generalService?.isNumber(mapCoordinate?.[0]) &&
                    this._generalService?.isNumber(mapCoordinate?.[1])
            );
            const currentCoordinate: L.LatLngExpression =
                this._generalService?.isNumber(
                    (dataPoints ?? this._markerList)?.[0]?.currentCoordinate?.[0]
                ) &&
                    this._generalService?.isNumber(
                        (dataPoints ?? this._markerList)?.[0]?.currentCoordinate?.[1]
                    )
                    ? (dataPoints ?? this._markerList)?.[0]?.currentCoordinate
                    : null;
            const line = L.polyline(mapCoordinates, {
                color: 'blue',
                weight: 3,
                opacity: 1,
                smoothFactor: 1,
            });
            line.bindTooltip('Distance covered').addTo(this._map);

            // Starting Point (First Marker)
            const startingPoint = L.marker(
                elem?.polygonData?.[0] as L.LatLngExpression,
                {
                    title: 'Start',
                    icon: L.icon({
                        iconUrl: 'assets/images/location-start-geo.svg',
                        iconSize: [40, 40],
                        iconAnchor: [16, 20],
                        popupAnchor: [0, -32],
                    }),
                }
            );
            startingPoint.addTo(this._map);

            // Final Point (Last Marker)
            const finalPoint = L.marker(
                elem?.polygonData?.[elem?.polygonData?.length - 1] as LatLngExpression,
                {
                    title: 'End',
                    icon: L.icon({
                        iconUrl: 'assets/images/location-end-geo.svg',
                        iconSize: [40, 40],
                        iconAnchor: [16, 20],
                        popupAnchor: [0, -32],
                    }),
                }
            );
            finalPoint.addTo(this._map);

            if (this.showStartEnd) {
                const startCoords = elem?.polygonData?.[0];
                const endCoords = elem?.polygonData?.[elem?.polygonData?.length - 1];

                const startPopup = L.popup({
                    closeButton: false,
                    autoClose: false,
                    closeOnClick: false,
                    offset: [0, -10],
                })
                    .setLatLng(startCoords as LatLngExpression)
                    .setContent('<b>Start</b><br>Loading...')
                    .addTo(this._map);

                const endPopup = L.popup({
                    closeButton: false,
                    autoClose: false,
                    closeOnClick: false,
                    offset: [0, -10],
                })
                    .setLatLng(endCoords as LatLngExpression)
                    .setContent('<b>End</b><br>Loading...')
                    .addTo(this._map);

                this.getLocationFromCoordinates(startCoords?.[0], startCoords?.[1])
                    ?.then((locationData) => {
                        startPopup.setContent(`<div class="d-flex gap5">
          <div><img src="../../../../assets/images/location-start-geo.svg" alt="Start" /></div>
          <div><b>Start</b><br>${locationData}</div>
        </div>`);
                    })
                    .catch((error) => {
                        console.error('Error fetching start location:', error);
                        startPopup.setContent('<b>Start</b><br>Location unavailable');
                    });

                this.getLocationFromCoordinates(endCoords?.[0], endCoords?.[1])
                    ?.then((locationData) => {
                        endPopup.setContent(`<div class="d-flex gap5">
          <div><img src="../../../../assets/images/location-end-geo.svg" alt="End" /></div>
          <div><b>End</b><br>${locationData}</div>
        </div>`);
                    })
                    .catch((error) => {
                        console.error('Error fetching end location:', error);
                        endPopup.setContent('<b>End</b><br>Location unavailable');
                    });
            }

            const customIcon = L.divIcon({
                className: 'custom-marker', // CSS class
                html: `
        <svg width="36" height="32" viewBox="0 0 36 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g filter="url(#filter0_d_27338_68581)" transform="rotate(-90, 18, 16)">
          <path d="M28.8993 5.74152C29.9842 4.90319 29.5537 3.17684 28.2136 2.99121L7.19947 0.0805532L6.55694 1.20339L12.3322 8.73321L10.802 18.2293L11.8884 18.8868L28.8993 5.74152Z" fill="#FDFDFD"/>
          <path d="M13.0874 8.84504C13.1212 8.63481 13.068 8.42118 12.9401 8.25448L7.47607 1.13036L7.59825 0.916843L28.0975 3.75618C28.7676 3.84899 28.9828 4.71217 28.4403 5.13134L11.8462 17.9545L11.6396 17.8295L13.0874 8.84504Z" stroke="url(#paint0_linear_27338_68581)" stroke-width="1.5373" stroke-linejoin="round"/>
        </g>
        <defs>
          <filter id="filter0_d_27338_68581" x="0.405485" y="0.0805664" width="35.2593" height="31.1046" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
            <feFlood flood-opacity="0" result="BackgroundImageFix"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dy="6.1492"/>
            <feGaussianBlur stdDeviation="3.0746"/>
            <feComposite in2="hardAlpha" operator="out"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_27338_68581"/>
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_27338_68581" result="shape"/>
          </filter>
          <linearGradient id="paint0_linear_27338_68581" x1="17.9019" y1="-2.80005" x2="22.9385" y2="15.9126" gradientUnits="userSpaceOnUse">
            <stop stop-color="#86AAD8"/>
            <stop offset="1" stop-color="#173C6D"/>
          </linearGradient>
        </defs>
      </svg> `,
                iconSize: [40, 40], // Adjust size
                iconAnchor: [15, 10], // Positioning
            });
            if (currentCoordinate) {
                this.traverseMarker = L.marker(currentCoordinate, {
                    icon: customIcon,
                }).addTo(this._map);
            }
            startingPoint.addTo(this._map);
            finalPoint.addTo(this._map);
            this._initialBounds = line.getBounds();
            this._map.fitBounds(line.getBounds());
            // this.traverseMarker = L.marker(currentCoordinate, { icon: customIcon }).addTo(this._map);
        });
    }

    /**
     * Leaflets circle
     */
    private _leafletCircle() {
        const circle = L.circle([8.534138, 76.883591], {
            color: 'red',
            fillColor: '#f03',
            fillOpacity: 0.5,
            radius: 50,
        })
            .bindTooltip('INFY Gate 1')
            .addTo(this._map);
    }

    /**
     * Images overlay
     */
    private _imageOverlay() {
        const imageUrl =
            'https://nsclusterhdistorage.blob.core.windows.net/navisafe-images/idec_avatar_1669370396190.jpg?st=2022-11-25T08%3A19%3A56Z&se=2030-12-31T23%3A59%3A59Z&sp=r&sv=2018-03-28&sr=b&sig=9J3%2BLBlNL7gyiEhR0FcsAnT%2Buoubxe98oBisG2iAs1M%3D',
            imageBounds: L.LatLngBoundsExpression = [
                [8.536876, 76.882741],
                [8.537162, 76.883599],
                [8.536664, 76.883963],
                [8.536006, 76.883427],
            ];
        L.imageOverlay(imageUrl, imageBounds, { interactive: true })
            .bindTooltip('UST Floor Plan')
            .addTo(this._map);
    }

    async getLocationFromCoordinates(latitude: number, longitude: number) {
        try {
            const response = await fetch(
                `${ApiConstants?.HERE_REVERSE_GEOCODE_URL}?at=${latitude},${longitude}&apiKey=${ApiConstants?.HERE_API_KEY}&lang=en-US`
            );
            const data = await response.json();
            if (data?.items?.[0]?.address?.label) {
                const location = data.items[0].address.label;
                return location;
            }
        } catch (error) {
            console.error(`Failed to fetch location `);
        }
    }

    async getAggregateDetails(entityId: string) {
        this.aggregatePayload.entityIds = [entityId];
        try {
            const result = await firstValueFrom(
                this._entityService.getEntityAggrLatestList(
                    this.aggregatePayload,
                    false
                ));
            return result;
        } catch (error) {
            console.error(`Failed to fetch aggregate details `);
            return null;
        }
    }

    /**
     * Leaflets circle polygon
     */
    private _leafletCirclePolygon() {
        if (this._existingCircleArray[0]) {
            const circleLayer = L.circle(
                [
                    this._existingCircleArray[0]?.[0]?.latitude,
                    this._existingCircleArray[0]?.[0]?.longitude,
                ],
                {
                    color: 'blue',
                    fillColor: '#fd7e14',
                    radius: this._existingCircleArray[0]?.[1]?.radius,
                }
            );
            circleLayer?.bindTooltip(this._selectedZoneName)?.addTo(this._map);
            this._circleLayers.set(this._selectedZoneName, circleLayer);
            this._map.fitBounds(circleLayer?.getBounds());
        } else if (this._existingPolygonArray.length > 0) {
            const polygonLayer = L.polygon(this._existingPolygonArray, {
                color: 'blue',
                fillColor: '#fd7e14',
            });
            polygonLayer?.bindTooltip(this._selectedZoneName)?.addTo(this._map);
            this._polygonLayers?.set(this._selectedZoneName, polygonLayer);
            this._map.fitBounds(polygonLayer?.getBounds());
        }
    }

    /**
     * Removes zone
     */
    private _removeZone() {
        if (this._circleLayers?.has(this._zoneNameToRemove)) {
            this._map?.removeLayer(this._circleLayers?.get(this._zoneNameToRemove));
            this._circleLayers?.delete(this._zoneNameToRemove);
        } else if (this._polygonLayers?.has(this._zoneNameToRemove)) {
            this._map?.removeLayer(this._polygonLayers?.get(this._zoneNameToRemove));
            this._polygonLayers?.delete(this._zoneNameToRemove);
        }
    }
}
